import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom"; // Import useParams
import { useSelector, useDispatch } from "react-redux";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import BodyHeader from "../../../components/common/CommonBodyHeader";
import CustomInput from "../../../components/common/CustomInput/CustomInput";
import CustomTable from "../../../components/common/CustomTable";
import { backdropLoadingAction } from "../../../redux/features/helperSlice";
import { getDriverDropdownDataAsync } from "../../../redux/features/fleetMaster";
import { listTripApiCall, listTripByPaginationApiCall } from "../../../Api/api";
import MISReport from "./MISReport";
import { ROLES } from "../../../constansts/Roles";
import TripTable from "./TripTable";
import { useDebounce } from "../../../Hooks/useDebounce";

export default function ContractualTripList() {
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const [params] = useSearchParams(); // Get trip_status from URL params
   const trip_status = params.get("trip_status");
   const review_status = params.get("review_status");

   const [records, setRecords] = useState([]);
   const [listPagination, setListPagination] = useState(null);
   const [limit, setLimit] = useState(10);
   const [page, setPage] = useState(0);
   const [tripStatusFilter, setTripStatusFilter] = useState(trip_status || ""); // Set initial filter from params
   const [searchTerm, setSearchTerm] = useState("");

   const debouncedSearchTerm = useDebounce(searchTerm, 500);

   const userRoleId = useSelector((state) => state?.auth?.user?.data?.user?.role_id);
   const userId = useSelector((state) => state?.auth?.user?.data?.user?.id);

   let roleName;
   for (const key in ROLES) {
      if (ROLES[key] === userRoleId) {
         roleName = key;
      }
   }

   const fetchTrips = useCallback(
      async (search = "") => {
         if (!roleName || !userId) return;

         dispatch(backdropLoadingAction(true));

         try {
            const { data: response } = await listTripByPaginationApiCall(
               roleName,
               userId,
               limit,
               page,
               trip_status,
               review_status,
               search
            );
            if (response.statusCode === 200) {
               setRecords(response?.data);
               setListPagination(response?.pagination);
            }
         } catch (error) {
            toast.error("Failed to fetch trips.");
         } finally {
            dispatch(backdropLoadingAction(false));
         }
      },
      [limit, page, roleName, userId, review_status, trip_status]
   );

   useEffect(() => {
      fetchTrips(debouncedSearchTerm);
   }, [fetchTrips, debouncedSearchTerm]);

   useEffect(() => {
      dispatch(getDriverDropdownDataAsync());
   }, []);

   const handleStatusFilter = (filterName, status) => {
      const newStatus = status === "all" ? "" : status;
      setTripStatusFilter(newStatus);
      setPage(0); // Reset to first page when filters change
      navigate(`/trip/contractual/view/?${filterName}=${newStatus}`); // Update URL
   };

   useEffect(() => {
      setPage(0); // Only once when debounced search term changes
   }, [debouncedSearchTerm]);

   const handleFilter = (e) => {
      const searchText = e.target.value.toLowerCase();

      setSearchTerm(searchText);
   };

   return (
      <div>
         <BodyHeader title="Trip List" />

         <div className="px-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
               <Stack direction="row" alignItems="center" spacing={1}>
                  <div>
                     <Typography>Trip Status</Typography>
                     <ToggleButtonGroup
                        color="primary"
                        value={trip_status}
                        exclusive
                        onChange={(_, value) => handleStatusFilter("trip_status", value)}
                        aria-label="Platform"
                     >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton value="open">Open Trip</ToggleButton>
                        <ToggleButton value="closed">Closed Trip</ToggleButton>
                     </ToggleButtonGroup>
                  </div>
                  <div>
                     <Typography>Review Status</Typography>
                     <ToggleButtonGroup
                        color="primary"
                        value={review_status}
                        exclusive
                        onChange={(_, value) =>
                           handleStatusFilter("review_status", value)
                        }
                        aria-label="Platform"
                     >
                        <ToggleButton value="">All</ToggleButton>
                        <ToggleButton value="pending">Pending</ToggleButton>
                        <ToggleButton value="approved">Approved</ToggleButton>
                        <ToggleButton value="rejected">Rejected</ToggleButton>
                     </ToggleButtonGroup>
                  </div>
               </Stack>
               <div className="d-flex gap-2">
                  <CustomInput
                     inputType="text"
                     placeholder="Search..."
                     id="search"
                     value={searchTerm}
                     onChange={(e) => handleFilter(e)}
                     containerStyle={{ marginRight: 20 }}
                  />
                  <MISReport />
               </div>
            </div>

            <TripTable
               rows={records}
               limit={limit}
               page={page}
               setLimit={setLimit}
               setPage={setPage}
               listPagination={listPagination}
            />
         </div>
      </div>
   );
}
